<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import YachtPageHero from "@/pages/YachtPage/YachtPageHero.vue";
import CustomButton from "@/components/Button/Button.vue";
import PhotoGallery from "@/components/PhotoGallery/PhotoGallery.vue";
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import RelatedYachts from "@/components/RelatedYachts/RelatedYachts.vue";
import {useYachtStore} from "@/stores/yachtStore";
import InquiryForm from "@/components/InquiryForm/InquiryForm.vue";
import {useInquiryStore} from "@/stores/inquiryStore";
import {useFavoritesStore} from "@/stores/favorites";
import {useSearchStore} from "@/stores/searchStore";
import {useFormatStore} from "@/stores/formatStore";
import {VueSpinner} from "vue3-spinners";


const router = useRouter();
const route = useRoute();

const yachtStore = useYachtStore();
const favoritesStore = useFavoritesStore();
const searchStore = useSearchStore();
const formatStore = useFormatStore();


const yachts = ref([
  {
    name: "Astoria",
    id: "12314324",
    slug: "astoria",
    category: "MOTOR SAILOR",
    image: new URL("@/assets/yacht.png", import.meta.url).href,
    length: "18,87",
    cabins: 5,
    berths: 10,
    destinations: [
      "French Polynesia",
      "Fiji Islands",
      "South East Asia",
      "Indian Ocean",
      "Australia",
    ],
    price: "38.000,00€",
  },
  {
    name: "Elysian",
    id: "78967544",
    slug: "elysian",
    category: "LUXURY YACHT",
    image: new URL("@/assets/yacht.png", import.meta.url).href,
    length: "24,50",
    cabins: 6,
    berths: 12,
    destinations: ["Mediterranean", "Croatia", "Greece", "Italy", "Turkey"],
    price: "52.000,00€",
  },
  {
    name: "High Five",
    id: "32487324",
    slug: "high-five",
    category: "LUXURY YACHT",
    image: new URL("@/assets/yacht.png", import.meta.url).href,
    length: "24,50",
    cabins: 6,
    berths: 12,
    destinations: ["Mediterranean", "Croatia", "Greece", "Italy", "Turkey"],
    price: "52.000,00€",
  },

  {
    name: "White Pearl",
    id: "8658469",
    slug: "white-pearl",
    category: "SAILING YACHT",
    image: new URL("@/assets/yacht.png", import.meta.url).href,
    length: "30,00",
    cabins: 4,
    berths: 8,
    destinations: ["Caribbean", "Bahamas", "Cuba", "Barbados", "St. Lucia"],
    price: "44.500,00€",
  },

]);


const inquiryStore = useInquiryStore();


const yacht = computed(() => {
  return yachtStore.selectedYacht;
}
)

const baseUrl = import.meta.env.VITE_APP_BASE_URL;


// const relatedYachts = computed(() =>
//     searchStore.yachts.filter((y) => y.category === yachtStore.selectedYacht.category && y.slug !== yachtStore.selectedYacht.slug)
// );


const submitData = computed(() => ({
  ...inquiryStore.singleYachtForm,
  inquiredYachts: yachtStore.selectedYacht ? `${baseUrl}/yachts/${yachtStore.selectedYacht.slug}`
      : null,
}));




const images = [
  { url: new URL('@/assets/about/image1.png', import.meta.url).href },
  { url: new URL('@/assets/about/image2.png', import.meta.url).href },
  { url: new URL('@/assets/about/image3.png', import.meta.url).href }
];

const formRef = ref();

onMounted(async () => {
  await searchStore.getAllYachts();
  yachtStore.selectedYachtSlug = route.params.slug as string;
  await yachtStore.fetchYachtDetails(yachtStore.selectedYachtSlug);

  if (route.query.scrollToForm === 'true') {

    setTimeout(() => {
      const formEl = document.getElementById('inquiry-form');
      if (formEl) {
        formEl.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  }
});

onUnmounted(() => {
  yachtStore.selectedYachtSlug = '';
});

watch(
    () => route.params.slug,
    async (newSlug) => {
      if (typeof newSlug === 'string') {
        await yachtStore.fetchYachtDetails(newSlug)
      }
    }
)

</script>

<template>

  <div>
    <YachtPageHero v-if="yachtStore.selectedYacht" :yacht="yachtStore.selectedYacht" />
    <div v-else class="grid place-content-center mt-10 h-[70vh]">
      <VueSpinner size="30" color="#beA5aa" />
    </div>
  </div>

  <div class="container mx-auto pt-24 px-4" id="scroll-to-yachtPage">

    <div class="responsive-title-1 font-thin text-center text-primary-darkBlue">About the Yacht</div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-20">
      <!-- Left Column -->
      <div class="flex flex-col gap-6 text-primary-darkBlue responsive-body-large">
        <h2 class="responsive-title-6 font-bold text-primary-violet">YACHT SPECIFICATIONS</h2>

        <div class="flex gap-4">
          <span class="text-primary-blueGrey">Yacht Name</span>
          <span class="font-bold">{{ yachtStore.selectedYacht?.name }}</span>
        </div>
        <div class="flex gap-4">
          <span class="text-primary-blueGrey">Yacht Model</span>
          <span class="font-bold">{{ yachtStore.selectedYacht?.model.name }}</span>
        </div>
        <div class="flex gap-4">
          <span class="text-primary-blueGrey">Yacht Type</span>
          <span class="font-bold uppercase">{{ yachtStore.selectedYacht?.category }}</span>
        </div>
        <div class="flex gap-3">
          <span class="text-primary-blueGrey">Yacht Engine</span>
          <span class="font-bold">{{ yachtStore.selectedYacht?.engine }}</span>
        </div>
        <!-- Array -->
        <div class="grid grid-cols-3 gap-4 p-5 bg-background-beige text-center text-primary-blueGrey responsive-body-medium font-semibold items-center">
          <div class="flex flex-col gap-2">
            <span>Cabins</span>
            <span class="text-primary-darkBlue font-bold responsive-title-6">{{ yachtStore.selectedYacht?.cabins }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <span>Heads</span>
            <span class="text-primary-darkBlue font-bold responsive-title-6">{{ yachtStore.selectedYacht?.heads }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <span>Berths</span>
            <span class="text-primary-darkBlue font-bold responsive-title-6">{{ yachtStore.selectedYacht?.berths }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <span>No of Crew</span>
            <span class="text-primary-darkBlue font-bold responsive-title-6">{{ yachtStore.selectedYacht?.crew }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <span>Length Overall</span>
            <span class="text-primary-darkBlue font-bold responsive-title-6">{{ yachtStore.selectedYacht?.lengthOverall }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <span>Beam Overall</span>
            <span class="text-primary-darkBlue font-bold responsive-title-6">{{ yachtStore.selectedYacht?.beamOverall }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <span>Fuel Consumption</span>
            <span class="text-primary-darkBlue font-bold responsive-title-6">{{ yachtStore.selectedYacht?.fuelConsumption }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <span>Cruising Speed</span>
            <span class="text-primary-darkBlue font-bold responsive-title-6">{{ yachtStore.selectedYacht?.cruisingSpeed }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <span>Built Year</span>
            <span class="text-primary-darkBlue font-bold responsive-title-6">{{ yachtStore.selectedYacht?.builtYear }}</span>
          </div>
        </div>

        <!-- Price/Week -->
        <h2 class="responsive-title-6 font-bold text-primary-violet mt-3">PRICE/WEEK</h2>
        <div class="text-primary-darkBlue responsive-body-xlarge font-bold">{{ formatStore.formatPrice(yachtStore.selectedYacht?.price) }}</div>

        <div class="flex flex-row gap-2 items-center">
          <!-- ADD TO FAVORITES Button -->
          <CustomButton
              :outlined="true"
              size="medium"
              :shadow="false"
              class="py-2 px-4 group fav-button"
              @click="favoritesStore.toggleFavorite(yachtStore.selectedYacht)"
          >
            <div
                v-if="!favoritesStore.isFavorite(yachtStore.selectedYacht?.id)"
                class="flex items-center gap-1"
            >
              <span>ADD TO FAVORITES</span>
              <img
                  src="@/assets/icons/favorite_blue.svg"
                  alt="Favorite"
                  class="w-5 h-5 block group-hover:hidden"
              />
              <img
                  src="@/assets/icons/favorite_white.svg"
                  alt="Favorite"
                  class="w-5 h-5 hidden group-hover:block"
              />
            </div>
            <div v-else class="remove-fav flex items-center gap-2">
              <span>REMOVE</span>
              <img
                  src="@/assets/icons/favorite_filled.svg"
                  alt="Favorite"
                  class="w-5 h-5 block"
              />
            </div>
          </CustomButton>

          <!-- MAKE INQUIRY Button -->
          <a href="#inquiry-form">
            <CustomButton type="red" size="medium" class="py-2 px-4 border border-primary-darkRed">
              <div class="flex items-center gap-2">
                <span>MAKE INQUIRY</span>
                <img
                    src="@/assets/icons/navigation_arrow.svg"
                    alt="Navigate"
                    class="w-6 h-6"
                />
              </div>
            </CustomButton>
          </a>
        </div>

      </div>

      <!-- Right Column -->
      <div class="flex flex-col gap-4 w-full">
        <h2 class="responsive-title-6 font-bold text-primary-violet">YACHT DESCRIPTION</h2>
        <div class="text-bw-midGrey responsive-body-xlarge">{{ yachtStore.selectedYacht?.description }}</div>
      </div>
    </div>

    <!-- Photo Gallery -->
    <div v-show="yachtStore.selectedYacht?.images.length" class="mt-24">
      <div class="responsive-title-1 font-thin text-center text-primary-darkBlue mb-10">Photo gallery</div>
      <PhotoGallery :images="yachtStore.selectedYacht?.images"/>
    </div>

    <!-- Tenders & Toys -->
    <div v-if="yachtStore.selectedYacht?.tendersToys.length > 0" class="mt-24">
      <div class="responsive-title-1 font-thin text-center text-primary-darkBlue">Tenders & Toys</div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-20">
        <!-- Right Column: Beach + Gym -->
        <div class="flex flex-col gap-10" v-for="(toys,key) in yachtStore.selectedYacht?.tendersToys" :key="key">
          <div >

            <div class="responsive-title-6 font-bold text-primary-violet mb-8 uppercase">
              {{ key }}
            </div>
            <ul class="list-disc list-inside space-y-2 text-bw-midGrey">
              <li v-for="(toy, index) in toys" :key="index">
                {{toy}}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>



  <div class="bg-background-beige mt-24 pt-20 md:p-40">
    <div class="flex flex-col gap-8 mb-10 px-5 responsive-body-large place-items-center text-center text-primary-darkBlue">
      <div class="responsive-title-1 font-thin">Interested in this yacht? </div>
      <div class="text-bw-midGrey font-semibold responsive-body-xlarge">Make an inquiry today and plan your next dream vacation with your selected yacht</div>
    </div>
    <section id="inquiry-form" class="scroll-mt-80 sm:px-5">
      <InquiryForm ref="formRef" :form-data="inquiryStore.singleYachtForm" :submit-data="submitData"/>
    </section>
  </div>



  <!-- Related Yachts -->
  <div v-if="yachtStore.relatedYachts.length > 0" class="container mt-24 pb-12">
    <div class="responsive-title-1 font-thin text-center text-primary-darkBlue">Related Yachts</div>
    <RelatedYachts :yachts="yachtStore.relatedYachts"/>
  </div>


<!--  </div>-->
</template>

<style scoped>

:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  width: 30px;
  height: 30px;
}

:deep(.swiper-button-prev:after),
:deep(.swiper-button-next:after) {
  font-size: 12px;
  color: #691736;
}


</style>
